import React, { Fragment, useState } from 'react';
import { ChevronRightIcon, FileTextIcon } from 'lucide-react';
import { StatementLoan } from '../../types/commission';
import { formatBps, formatUsd, sumCents } from '../../utils/format';
import { Badge } from '../sf/Badge';
import { StoredReference } from '../sf/StoredReference';
import { Button } from '../sf/Button';
import { NewBadge } from '../sf/NewBadge';
import { Card } from '../sf/Card';
import { Td, Th } from '../sf/DataTable';
import { Money } from '../sf/Money';
export function StatementLoansTable({
  loans
}: {
  loans: StatementLoan[];
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['LN-2026-004744', 'LN-2026-004790']));
  const allExpanded = expanded.size === loans.length;
  const toggle = (loanNumber: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(loanNumber)) next.delete(loanNumber);else next.add(loanNumber);
    return next;
  });
  const totals = {
    amount: sumCents(loans.map(loan => loan.loanAmount)),
    adjusted: sumCents(loans.map(loan => loan.adjustedAmount)),
    pay: sumCents(loans.map(loan => loan.payTotal)),
    adjustedComp: sumCents(loans.map(loan => loan.adjustedTotal))
  };
  return <Card title={`Loans funded (${loans.length})`} icon={FileTextIcon} iconBg="#3BA755" meta="Expand a loan to see the commission lines behind it" bodyClassName="" actions={<Button size="sm" onClick={() => setExpanded(allExpanded ? new Set() : new Set(loans.map(loan => loan.loanNumber)))} data-id="element-997" data-mp="2f9wb36t3s1wg">
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </Button>} data-id="element-996" data-mp="26d58scnyjqtf">
      <div className="overflow-x-auto" data-id="element-998" data-mp="2u4j2alxuhsfz">
        <table className="w-full border-collapse" data-id="element-999" data-mp="2u3hr8hgtx5y9">
          <thead data-id="element-1000" data-mp="urorid2npmhu">
            <tr data-id="element-1001" data-mp="us85xjt3lsr4">
              <Th className="w-10" data-id="element-1002" data-mp="usqcb99h5ncr">
                <span className="sr-only" data-id="element-1003" data-mp="utrqhmlsepyp">Expand</span>
              </Th>
              <Th data-id="element-1004" data-mp="zrkv4b7fzbwv">Loan #</Th>
              <Th data-id="element-1005" data-mp="zsvzac4suloi">Borrower</Th>
              <Th data-id="element-1006" data-mp="rpu3061emdnh">Funded</Th>
              <Th align="right" data-id="element-1007" data-mp="rr55m2j45fd0">Loan Amount</Th>
              <Th align="right" data-id="element-1008" data-mp="rrwwj01rmowy">Adjusted Amount</Th>
              <Th align="right" data-id="element-1009" data-mp="msam0ydbnmru">BPS</Th>
              <Th align="right" data-id="element-1010" data-mp="mtdbd1uzxh5n">
                Adjusted
                <span className="block text-[11px] font-normal text-sf-weak" data-id="element-1011" data-mp="11p1cuj8xjq5u">adj. loan × BPS · pays</span>
              </Th>
              <Th align="right" className="font-normal" data-id="element-1012" data-mp="wnvi6fvplsto">
                Stored (unadjusted)
                <span className="block text-[11px] font-normal text-sf-weak" data-id="element-1013" data-mp="1an4yrgntuoy6">reference</span>
              </Th>
            </tr>
          </thead>
          <tbody data-id="element-1014" data-mp="1kehpu61sxb35">
            {loans.map(loan => {
            const isOpen = expanded.has(loan.loanNumber);
            const rowId = `lines-${loan.loanNumber}`;
            return <Fragment key={loan.loanNumber} data-id="element-1015" data-mp="1tf63v0r73bg1">
                  <tr className={isOpen ? 'bg-[#FAFAF9]' : ''} data-id="element-1016" data-mp="1tfz0va0cqmkz">
                    <Td className="pr-0" data-id="element-1017" data-mp="1penav3dkfzc9">
                      <button type="button" aria-expanded={isOpen} aria-controls={rowId} aria-label={`${isOpen ? 'Collapse' : 'Expand'} lines for ${loan.loanNumber}`} onClick={() => toggle(loan.loanNumber)} className="flex h-6 w-6 items-center justify-center rounded text-sf-weak transition-colors duration-150 ease-out hover:bg-sf-bg hover:text-sf-text" data-id="element-1018" data-mp="1pfoxm53y2a9t">
                        <ChevronRightIcon className={`h-4 w-4 transition-transform duration-150 ease-out ${isOpen ? 'rotate-90' : ''}`} data-id="element-1019" data-mp="1u2d8c5hk5f4m" />
                      </button>
                    </Td>
                    <Td className="whitespace-nowrap font-semibold text-sf-blue" data-id="element-1020" data-mp="1g95iai94krp1">{loan.loanNumber}</Td>
                    <Td data-id="element-1021" data-mp="1ka7lfa65xnwa">
                      <span className="flex flex-wrap items-center gap-1.5" data-id="element-1022" data-mp="16c97g5vr7thb">
                        {loan.borrower}
                        {loan.negativeComp && <Badge tone="warning" data-id="element-1023" data-mp="1bbcefvvao9rx">Negative comp</Badge>}
                        {loan.split && <>
                            <Badge tone="info" data-id="element-1024" data-mp="29noqnyxldy0t">Split {loan.split.pct}%</Badge>
                            <NewBadge label="NEW" data-id="element-1025" data-mp="29psy2qup9f5u" />
                          </>}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap" data-id="element-1026" data-mp="1zwu2gp0scocq">{loan.fundedDate}</Td>
                    <Td align="right" data-id="element-1027" data-mp="1uzboulbtpkye">{formatUsd(loan.loanAmount)}</Td>
                    <Td align="right" className={loan.negativeComp ? 'font-semibold text-sf-amber-text' : ''} data-id="element-1028" data-mp="1uwfs30bpp2bj">
                      {formatUsd(loan.adjustedAmount)}
                    </Td>
                    <Td align="right" data-id="element-1029" data-mp="1m086jjkhd9ky">
                      {loan.baseBps} <span className="text-sf-weak" data-id="element-1030" data-mp="1h02lwncmoncd">+ {loan.bonusBps}</span>
                    </Td>
                    <Td align="right" className="font-semibold" data-id="element-1031" data-mp="3gb3q1gx7e92i">
                      <Money value={loan.adjustedTotal} data-id="element-1032" data-mp="3bdbmfpn74yih" />
                    </Td>
                    <Td align="right" data-id="element-1033" data-mp="3bb7l9fgomdlw">
                      <StoredReference value={loan.payTotal} compact data-id="element-1034" data-mp="3ixcretpauuxt" />
                    </Td>
                  </tr>
                  {isOpen && <tr id={rowId} data-id="element-1035" data-mp="3ntl0pqgtftx2">
                      <td colSpan={9} className="border-b border-sf-border bg-[#FAFAF9] pb-3 pl-12 pr-3" data-id="element-1036" data-mp="3sluph3672nxr">
                        {(loan.split || loan.negativeComp) && <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-sf-weak" data-id="element-1037" data-mp="1hwbtzppopqv">
                            {loan.split && `Split ${loan.split.pct} / ${loan.split.partnerPct} with ${loan.split.partner} (splits are NEW — not in main today). Alex’s ${loan.split.pct}% is paid at his own rates; unit credit stays with Alex.`}
                            {loan.negativeComp && <>
                                <span data-id="element-1038" data-mp="p54gqgj89wsd">
                                  Total Comp Corr 0.60% ($1,192.80) − $1,395.00 halved fee short = −$202.20: revenue after fee short is negative → comp % &lt; 0 → Adjusted Loan
                                  Amount {formatUsd(loan.adjustedAmount)} (main).
                                </span>
                                <NewBadge label="NEW / requested: flat $500 LO / $250 TL — not applied" data-id="element-1039" data-mp="2z8l6ofdsw1ao" />
                              </>}
                          </div>}
                        <table className="w-full border-collapse overflow-hidden rounded border border-sf-border bg-white" data-id="element-1040" data-mp="26j4kw0korinf">
                          <thead data-id="element-1041" data-mp="22jlx8x1hctgw">
                            <tr data-id="element-1042" data-mp="22ghmthxf91hs">
                              <Th data-id="element-1043" data-mp="2ga8vyp78qjcz">Component</Th>
                              <Th data-id="element-1044" data-mp="2gb0kjg2f7gtr">Rule Key</Th>
                              <Th align="right" data-id="element-1045" data-mp="2bih50dk6abmb">BPS</Th>
                              <Th align="right" data-id="element-1046" data-mp="2bgwagnxuysog">Adjusted (pays)</Th>
                              <Th align="right" className="font-normal" data-id="element-1047" data-mp="1ns3o9j71fz71">
                                Stored – reference
                              </Th>
                            </tr>
                          </thead>
                          <tbody data-id="element-1048" data-mp="3ugds8z8h8wx3">
                            {loan.lines.map(line => <tr key={line.component} data-id="element-1049" data-mp="34anx5dfzgiu">
                                <Td className="py-1.5" data-id="element-1050" data-mp="3lhii5c2bb2oi">
                                  {line.component}
                                  {loan.split && <span className="text-sf-weak" data-id="element-1051" data-mp="3qgv6604x6fso"> · {loan.split.pct}%</span>}
                                </Td>
                                <Td className="py-1.5 font-mono text-xs" data-id="element-1052" data-mp="hy5qxuqx2bm4">{line.ruleKey}</Td>
                                <Td align="right" className="py-1.5" data-id="element-1053" data-mp="m12elzvbcxwx">
                                  {formatBps(line.bps, line.component === 'Monthly Bonus')}
                                </Td>
                                <Td align="right" className="py-1.5 font-semibold" data-id="element-1054" data-mp="czcem5nd8qnj">
                                  <Money value={line.adjustedAmount} data-id="element-1055" data-mp="2sqqgw06e3nub" />
                                </Td>
                                <Td align="right" className="py-1.5" data-id="element-1056" data-mp="2xsz9gvfconvw">
                                  <StoredReference value={line.payAmount} compact data-id="element-1057" data-mp="3pg0pqv8nr4bm" />
                                </Td>
                              </tr>)}
                          </tbody>
                        </table>
                      </td>
                    </tr>}
                </Fragment>;
          })}
            <tr className="bg-[#FAFAF9] font-semibold" data-id="element-1058" data-mp="bzta5nupdt8o">
              <Td data-id="element-1059" data-mp="bzb5q3xw08po" />
              <Td colSpan={3} data-id="element-1060" data-mp="c1xeg6wiku9h">
                Loan subtotal
              </Td>
              <Td align="right" data-id="element-1061" data-mp="71rrv553pxy9">{formatUsd(totals.amount)}</Td>
              <Td align="right" data-id="element-1062" data-mp="29hyff4s2fr8">{formatUsd(totals.adjusted)}</Td>
              <Td data-id="element-1063" data-mp="3tgkkxt9o7xv4" />
              <Td align="right" data-id="element-1064" data-mp="3th47jna9nafh">
                <Money value={totals.adjustedComp} data-id="element-1065" data-mp="3tg1fvd54d3yi" />
              </Td>
              <Td align="right" data-id="element-1066" data-mp="uqu7k7g6dgq4">
                <StoredReference value={totals.pay} compact data-id="element-1067" data-mp="us5ak51sqkg6" />
              </Td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>;
}